import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Select, Spin, notification } from "antd";
import {
  DislikeOutlined,
  LikeOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { fetchOrgCxGptMessageFeedback } from "util/copilotSessionApi";
import {
  avatarColorForUser,
  formatSessionDateTime,
  userInitials,
} from "util/advisorOrgHistoryDisplay";
import styles from "./style.module.scss";

const PERIOD_DAYS = {
  "90": 90,
  "30": 30,
  "7": 7,
  all: null,
};

const AdvisorFeedbackPanel = ({ user, locale = "tr-TR", theme = "dark" }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total: 0 });
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState("90");
  const [sentiment, setSentiment] = useState(undefined);

  const startDate = useMemo(() => {
    const days = PERIOD_DAYS[period];
    if (!days) return undefined;
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
  }, [period]);

  const fetchList = useCallback(
    async (page = 1) => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const payload = { page, page_size: 50 };
        if (search.trim()) payload.search = search.trim();
        if (sentiment) payload.sentiment = sentiment;
        if (startDate) payload.start_date = startDate;

        const result = await fetchOrgCxGptMessageFeedback(payload);
        if (result?.success) {
          setItems(result.feedback || []);
          setSummary(result.summary || null);
          setPagination(result.pagination || { page: 1, page_size: 50, total: 0 });
        } else if (result?.error && String(result.error).toUpperCase().includes("FORBIDDEN")) {
          notification.error({ message: t("advisor_history_page.forbidden") });
        } else {
          notification.error({
            message: result?.error || t("advisor_history_page.feedbackLoadError"),
          });
        }
      } catch {
        notification.error({ message: t("advisor_history_page.feedbackLoadError") });
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, search, sentiment, startDate, t]
  );

  useEffect(() => {
    fetchList(1);
  }, [fetchList]);

  const wrapperClass =
    theme === "light" || theme === "brand"
      ? `${styles.feedbackPanel} ${styles.wrapperLight}`
      : styles.feedbackPanel;

  return (
    <div className={wrapperClass}>
      <div className={styles.feedbackStatsRow}>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{summary?.total ?? "—"}</span>
          <span className={styles.statLabel}>{t("advisor_history_page.feedbackStatTotal")}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{summary?.positive ?? "—"}</span>
          <span className={styles.statLabel}>{t("advisor_history_page.feedbackStatPositive")}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{summary?.negative ?? "—"}</span>
          <span className={styles.statLabel}>{t("advisor_history_page.feedbackStatNegative")}</span>
        </div>
      </div>

      <div className={styles.toolbar}>
        <Input
          className={styles.searchInput}
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t("advisor_history_page.feedbackSearchPlaceholder")}
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          onPressEnter={() => setSearch(searchDraft)}
        />
        <Select
          value={period}
          onChange={setPeriod}
          style={{ minWidth: 140 }}
          options={[
            { value: "90", label: t("advisor_history_page.period90") },
            { value: "30", label: t("advisor_history_page.period30") },
            { value: "7", label: t("advisor_history_page.period7") },
            { value: "all", label: t("advisor_history_page.periodAll") },
          ]}
        />
        <Select
          allowClear
          placeholder={t("advisor_history_page.feedbackSentimentAll")}
          value={sentiment}
          onChange={setSentiment}
          style={{ minWidth: 140 }}
          options={[
            { value: "positive", label: t("advisor_history_page.feedbackPositive") },
            { value: "negative", label: t("advisor_history_page.feedbackNegative") },
          ]}
        />
        <Button icon={<SearchOutlined />} onClick={() => setSearch(searchDraft)}>
          {t("advisor_history_page.search")}
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => fetchList(1)}>
          {t("advisor_history_page.refresh")}
        </Button>
      </div>

      <div className={styles.feedbackList}>
        <div className={styles.feedbackListHeader}>
          <span>{t("advisor_history_page.colUser")}</span>
          <span>{t("advisor_history_page.feedbackColSentiment")}</span>
          <span>{t("advisor_history_page.feedbackColExcerpt")}</span>
          <span>{t("advisor_history_page.colDate")}</span>
        </div>
        {loading ? (
          <div className={styles.loadingWrap}>
            <Spin />
          </div>
        ) : items.length === 0 ? (
          <div className={styles.emptyState}>{t("advisor_history_page.feedbackEmpty")}</div>
        ) : (
          items.map((row) => (
            <div key={row.id} className={styles.feedbackRow}>
              <div className={styles.userCell}>
                <span
                  className={styles.avatar}
                  style={{ backgroundColor: avatarColorForUser(row.user?.user_id) }}
                >
                  {userInitials(row.user?.full_name, row.user?.email)}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className={styles.userName}>{row.user?.full_name}</div>
                  <div className={styles.userEmail}>{row.user?.email}</div>
                </div>
              </div>
              <div className={styles.feedbackSentimentCell}>
                {row.sentiment === "positive" ? (
                  <LikeOutlined className={styles.feedbackIconPositive} />
                ) : (
                  <DislikeOutlined className={styles.feedbackIconNegative} />
                )}
              </div>
              <div className={styles.feedbackExcerptCell}>
                <div className={styles.sessionTitle}>
                  {row.session_title || t("advisor_history_page.feedbackUntitledSession")}
                </div>
                <div className={styles.feedbackExcerpt}>
                  {row.content_snapshot || "—"}
                </div>
              </div>
              <div className={styles.dateCell}>
                {formatSessionDateTime(row.created_at, locale)}
              </div>
            </div>
          ))
        )}
      </div>
      {pagination.total > items.length ? (
        <div className={styles.loadMore}>
          <Button
            type="link"
            loading={loading}
            onClick={() => fetchList(pagination.page + 1)}
          >
            {t("advisor_history_page.loadMore")}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default AdvisorFeedbackPanel;
